import { useMemo } from 'react';
import { api } from '../../lib/api';
import { useHeldResource } from '../../lib/hooks';

/**
 * The review's Asset Management slides (slide 10): the client's ACTIVE
 * Autotask configuration items, grouped by category, over as many pages as
 * they take. Data: GET /am/clients/:id/autotask/assets (routes/amAutotask.ts),
 * the client page's Assets tab — trimmed here to what the slide shows before
 * anything is kept on the review.
 */

export interface ReviewAsset {
  id: number;
  name: string;
  category: string;
  product: string | null;
  lastUser: string | null;
  warrantyExpires: string | null;
}
/** What the slides keep on the review. */
export interface AssetsSnap {
  linked: boolean;
  companyName: string | null;
  assets: ReviewAsset[];
}

interface Raw {
  linked: boolean;
  companyName: string | null;
  assets: {
    id: number;
    name: string;
    hostname: string | null;
    category: string | null;
    type: string | null;
    product: string | null;
    lastUser: string | null;
    warrantyExpires: string | null;
    active: boolean;
  }[];
}

/** Active assets only, the fields the slide shows, by category then name. */
export function assetsOnly(r: Raw): AssetsSnap {
  return {
    linked: r.linked,
    companyName: r.companyName,
    assets: (r.assets ?? [])
      .filter((a) => a.active)
      .map((a) => ({
        id: a.id,
        name: a.name || a.hostname || 'Device',
        category: a.category || a.type || 'Uncategorised',
        product: a.product,
        lastUser: a.lastUser,
        warrantyExpires: a.warrantyExpires,
      }))
      .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name)),
  };
}

export async function loadReviewAssets(clientId: string, refresh: boolean): Promise<AssetsSnap> {
  return assetsOnly(await api.get<Raw>(`/am/clients/${clientId}/autotask/assets${refresh ? '?refresh=1' : ''}`));
}

/* A page holds this many lines; a category heading is a line too. */
const LINES_PER_PAGE = 18;

export type AssetLine = { kind: 'category'; name: string; count: number; continued: boolean } | { kind: 'asset'; asset: ReviewAsset };

/**
 * The assets cut into slide pages. Each category starts with its heading; a
 * category that runs over a page starts the next with its heading again,
 * marked "continued". A heading is never left alone at the foot of a page.
 */
export function assetPages(assets: ReviewAsset[]): AssetLine[][] {
  const counts = new Map<string, number>();
  for (const a of assets) counts.set(a.category, (counts.get(a.category) ?? 0) + 1);
  const pages: AssetLine[][] = [];
  let page: AssetLine[] = [];
  let current: string | null = null;
  for (const a of assets) {
    const newCategory = a.category !== current;
    /* Room for the asset, and for its heading when it starts a category. */
    if (page.length + (newCategory ? 2 : 1) > LINES_PER_PAGE) {
      pages.push(page);
      page = [];
      if (!newCategory) page.push({ kind: 'category', name: a.category, count: counts.get(a.category) ?? 0, continued: true });
    }
    if (newCategory) {
      page.push({ kind: 'category', name: a.category, count: counts.get(a.category) ?? 0, continued: false });
      current = a.category;
    }
    page.push({ kind: 'asset', asset: a });
  }
  if (page.length) pages.push(page);
  return pages;
}

/** Every asset under its category heading, in one run — for the form's preview. */
function allLines(assets: ReviewAsset[]): AssetLine[] {
  const counts = new Map<string, number>();
  for (const a of assets) counts.set(a.category, (counts.get(a.category) ?? 0) + 1);
  const out: AssetLine[] = [];
  let current: string | null = null;
  for (const a of assets) {
    if (a.category !== current) {
      out.push({ kind: 'category', name: a.category, count: counts.get(a.category) ?? 0, continued: false });
      current = a.category;
    }
    out.push({ kind: 'asset', asset: a });
  }
  return out;
}

const day = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** One page of the slide: category headings with their assets under them. */
export function AssetsTable({ lines }: { lines: AssetLine[] }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <table className="am-asset-table">
      <thead>
        <tr>
          <th>Device</th>
          <th>Product</th>
          <th>Last user</th>
          <th>Warranty</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) =>
          l.kind === 'category' ? (
            <tr key={`c-${l.name}-${i}`} className="am-asset-cat">
              <td colSpan={4}>
                {l.name} <span>· {l.count}{l.continued ? ' · continued' : ''}</span>
              </td>
            </tr>
          ) : (
            <tr key={l.asset.id}>
              <td title={l.asset.name}>{l.asset.name}</td>
              <td title={l.asset.product ?? ''}>{l.asset.product ?? '—'}</td>
              <td title={l.asset.lastUser ?? ''}>{l.asset.lastUser ?? '—'}</td>
              <td className={l.asset.warrantyExpires && l.asset.warrantyExpires < today ? 'is-expired' : undefined}>
                {day(l.asset.warrantyExpires)}
              </td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

/** The review form's preview of slide 10: how many assets and slides, and the
 *  list itself, in the slide's own table — one run, not cut into pages. */
export function AssetsPreview({ clientId }: { clientId: string }) {
  const data = useHeldResource<Raw>(`/am/clients/${clientId}/autotask/assets`);
  const snap = useMemo(() => (data.data ? assetsOnly(data.data) : null), [data.data]);
  if (data.loading && !snap) return <p className="small muted">Reading this client&apos;s assets from Autotask…</p>;
  if (data.error) return <p className="small muted">Could not read the client&apos;s assets from Autotask: {data.error}</p>;
  if (!snap) return null;
  if (!snap.linked) return <p className="small muted">This client is not linked to an Autotask company, so the slide has no assets to show.</p>;
  const cats = new Map<string, number>();
  for (const a of snap.assets) cats.set(a.category, (cats.get(a.category) ?? 0) + 1);
  const pages = assetPages(snap.assets).length;
  return (
    <div className="am-csat-preview">
      <p className="xs muted">
        Filled automatically from Autotask for {snap.companyName}: {snap.assets.length} active asset{snap.assets.length === 1 ? '' : 's'}
        {pages > 1 ? `, over ${pages} slides` : ''}. The slides keep their own copy from the day they are presented.
      </p>
      {snap.assets.length === 0 ? (
        <p className="small muted">No active assets.</p>
      ) : (
        <>
          <p className="small">
            {[...cats].map(([c, n]) => `${c} ${n}`).join(' · ')}
          </p>
          <div className="am-asset-preview-list">
            <AssetsTable lines={allLines(snap.assets)} />
          </div>
        </>
      )}
    </div>
  );
}
